import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const empty = {
  business_name: "", contact_person: "", mobile: "", whatsapp: "", email: "", address: "",
  category: "MSME", priority: "Warm", loan_requirement: true, loan_category: "", loan_amount: "",
  mela_invited: false, mela_attended: false, rm_username: "", mela_interest: "",
};

export default function AddCustomer() {
  const { meta, user } = useAuth();
  const navigate = useNavigate();
  const [f, setF] = useState(empty);
  const [rms, setRms] = useState([]);
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  useEffect(() => {
    if (user.role === "admin") api.get("/rms").then((r) => setRms(r.data));
  }, [user]);

  const submit = async (e) => {
    e.preventDefault();
    if (!f.business_name || !f.mobile) return toast.error("Business name and mobile are required");
    if (user.role === "admin" && !f.rm_username) return toast.error("Please assign this customer to an RM");
    if (!f.mela_interest) return toast.error("Please answer: interested to attend Loan Mela?");
    setLoading(true);
    try {
      const payload = { ...f, loan_amount: Number(f.loan_amount) || 0 };
      const { data } = await api.post("/customers", payload);
      toast.success("Customer added");
      navigate(`/app/customers/${data.id}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <PageHeader title="Add Customer" subtitle="Capture a new loan lead into your pipeline." />
      <form onSubmit={submit} className="space-y-6 max-w-4xl">
        {user.role === "admin" && (
          <Card className="p-6">
            <div className="text-sm font-bold text-slate-700 mb-4 uppercase tracking-wider">Assign to RM</div>
            <Select value={f.rm_username} onValueChange={(v) => set("rm_username", v)}>
              <SelectTrigger data-testid="cust-rm-assign" className="max-w-sm"><SelectValue placeholder="Select an RM" /></SelectTrigger>
              <SelectContent>{rms.map((r) => <SelectItem key={r.username} value={r.username}>{r.name} (@{r.username})</SelectItem>)}</SelectContent>
            </Select>
          </Card>
        )}
        <Card className="p-6">
          <div className="text-sm font-bold text-slate-700 mb-4 uppercase tracking-wider">Business Details</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Business Name *"><Input data-testid="cust-business-name" value={f.business_name} onChange={(e) => set("business_name", e.target.value)} /></Field>
            <Field label="Contact Person"><Input data-testid="cust-contact" value={f.contact_person} onChange={(e) => set("contact_person", e.target.value)} /></Field>
            <Field label="Mobile *"><Input data-testid="cust-mobile" value={f.mobile} onChange={(e) => set("mobile", e.target.value)} /></Field>
            <Field label="WhatsApp"><Input data-testid="cust-whatsapp" value={f.whatsapp} onChange={(e) => set("whatsapp", e.target.value)} /></Field>
            <Field label="Email"><Input data-testid="cust-email" value={f.email} onChange={(e) => set("email", e.target.value)} /></Field>
            <Field label="Address"><Input data-testid="cust-address" value={f.address} onChange={(e) => set("address", e.target.value)} /></Field>
          </div>
        </Card>

        <Card className="p-6">
          <div className="text-sm font-bold text-slate-700 mb-4 uppercase tracking-wider">Categorisation & Loan</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Category">
              <Select value={f.category} onValueChange={(v) => set("category", v)}>
                <SelectTrigger data-testid="cust-category"><SelectValue /></SelectTrigger>
                <SelectContent>{meta.categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Priority">
              <Select value={f.priority} onValueChange={(v) => set("priority", v)}>
                <SelectTrigger data-testid="cust-priority"><SelectValue /></SelectTrigger>
                <SelectContent>{meta.priorities.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Loan Category">
              <Select value={f.loan_category} onValueChange={(v) => set("loan_category", v)}>
                <SelectTrigger data-testid="cust-loan-category"><SelectValue placeholder="Select loan category" /></SelectTrigger>
                <SelectContent>{(meta.loan_categories || []).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Loan Amount (₹)"><Input data-testid="cust-loan-amount" type="number" value={f.loan_amount} onChange={(e) => set("loan_amount", e.target.value)} /></Field>
            <Field label="Interested to attend our Loan Mela? *">
              <Select value={f.mela_interest} onValueChange={(v) => set("mela_interest", v)}>
                <SelectTrigger data-testid="cust-mela-interest"><SelectValue placeholder="Select Yes / No" /></SelectTrigger>
                <SelectContent><SelectItem value="Yes">Yes</SelectItem><SelectItem value="No">No</SelectItem></SelectContent>
              </Select>
            </Field>
          </div>
          <div className="flex flex-wrap gap-8 mt-5">
            <Toggle label="Loan Requirement" checked={f.loan_requirement} onCheckedChange={(v) => set("loan_requirement", v)} testid="cust-loan-req" />
            <Toggle label="Mela Invited" checked={f.mela_invited} onCheckedChange={(v) => set("mela_invited", v)} testid="cust-mela-invited" />
            <Toggle label="Mela Attended" checked={f.mela_attended} onCheckedChange={(v) => set("mela_attended", v)} testid="cust-mela-attended" />
          </div>
        </Card>

        <div className="flex gap-3">
          <Button type="submit" data-testid="add-customer-submit-btn" disabled={loading} className="bg-[#0F4C81] hover:bg-[#0A3A66]">{loading ? "Saving…" : "Save Customer"}</Button>
          <Button type="button" variant="outline" onClick={() => navigate("/app/customers")}>Cancel</Button>
        </div>
      </form>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div><Label className="mb-1.5 block">{label}</Label>{children}</div>
);
const Toggle = ({ label, checked, onCheckedChange, testid }) => (
  <div className="flex items-center gap-3"><Switch checked={checked} onCheckedChange={onCheckedChange} data-testid={testid} /><Label>{label}</Label></div>
);
